import React, { useState, useEffect } from 'react';
import Header from '../../components/layout/Header';
import { tenantService } from '../../services/tenantService';
import { userService } from '../../services/userService';
import { ticketService } from '../../services/ticketService';
import { TicketStats } from '../../types/ticket';

/* -- Stat Card ------------------------------------------------- */
interface StatCardProps {
  label: string;
  value: string;
  icon: string;
  iconColor: string;
  iconBg: string;
  trend?: { value: string; isPositive: boolean };
  footer?: React.ReactNode;
  progress?: number;
  loading?: boolean;
}

const StatCard: React.FC<StatCardProps> = ({
  label,
  value,
  icon,
  iconColor,
  iconBg,
  trend,
  footer,
  progress,
  loading,
}) => (
  <div className="bg-white border border-slate-200 hover:border-primary/40 transition-all p-6 rounded-xl group">
    <div className="flex items-center justify-between mb-4">
      <p className="text-slate-500 text-sm font-medium">{label}</p>
      <span className={`material-symbols-outlined ${iconColor} ${iconBg} p-2 rounded-lg`}>
        {icon}
      </span>
    </div>
    <div className="flex items-baseline gap-2">
      {loading ? (
        <div className="w-16 h-8 bg-slate-100 rounded animate-pulse" />
      ) : (
        <h3 className="text-3xl font-extrabold text-slate-900">{value}</h3>
      )}
      {trend && !loading && (
        <p className={`text-xs font-bold flex items-center ${trend.isPositive ? 'text-green-500' : 'text-primary'}`}>
          {trend.value}
        </p>
      )}
    </div>
    {progress !== undefined && !loading && (
      <div className="mt-4 w-full bg-slate-100 h-1 rounded-full overflow-hidden">
        <div className="bg-primary h-full" style={{ width: `${progress}%` }} />
      </div>
    )}
    {footer && !loading && <p className="text-xs text-slate-500 mt-4 tracking-wide">{footer}</p>}
  </div>
);

/* -- Activity Item --------------------------------------------- */
interface ActivityItemProps {
  icon: string;
  iconColor: string;
  iconBg: string;
  title: string;
  description: string;
  time: string;
  showLine?: boolean;
}

const ActivityItem: React.FC<ActivityItemProps> = ({
  icon,
  iconColor,
  iconBg,
  title,
  description,
  time,
  showLine = true,
}) => (
  <div className="flex gap-4 relative">
    {showLine && (
      <div className="absolute left-4 top-10 bottom-0 w-[1px] bg-slate-200 h-full" />
    )}
    <div className={`size-8 rounded-full ${iconBg} border ${iconBg.replace('/10', '/20')} flex items-center justify-center ${iconColor} shrink-0`}>
      <span className="material-symbols-outlined text-lg">{icon}</span>
    </div>
    <div>
      <p className="text-sm font-bold text-slate-800">{title}</p>
      <p className="text-xs text-slate-500 mt-1">{description}</p>
      <p className="text-[10px] text-slate-400 mt-2 font-medium">{time}</p>
    </div>
  </div>
);

/* -- Dashboard Page -------------------------------------------- */
const DashboardPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [brandCount, setBrandCount] = useState(0);
  const [activeBrandCount, setActiveBrandCount] = useState(0);
  const [userCount, setUserCount] = useState(0);
  const [ticketStats, setTicketStats] = useState<TicketStats | null>(null);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [brandsRes, usersRes, ticketsRes] = await Promise.allSettled([
          tenantService.getAll(1, 1),
          userService.getSystemAdmins(),
          ticketService.getStats(),
        ]);

        if (brandsRes.status === 'fulfilled' && brandsRes.value.success) {
          setBrandCount(brandsRes.value.data.totalCount);
        }

        if (usersRes.status === 'fulfilled') {
          setUserCount(usersRes.value.length);
        }

        if (ticketsRes.status === 'fulfilled' && ticketsRes.value.success) {
          setTicketStats(ticketsRes.value.data);
        }

        if (brandsRes.status === 'fulfilled' && brandsRes.value.success) {
          const totalCount = brandsRes.value.data.totalCount;
          setBrandCount(totalCount);

          try {
            const allBrandsRes = await tenantService.getAll(1, Math.min(totalCount, 100));
            if (allBrandsRes.success) {
              setActiveBrandCount(allBrandsRes.data.items.filter(b => b.isActive).length);
            }
          } catch {
            // fallback
          }
        }
      } catch {
        // Silent fail — stats will show 0
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const openTickets = ticketStats ? ticketStats.openTickets + ticketStats.inProgressTickets : 0;
  const urgentTickets = ticketStats?.urgentTickets || 0;

  return (
    <main className="flex-1 flex flex-col h-screen overflow-y-auto bg-slate-50">
      <Header
        title="Platform Overview"
        searchPlaceholder="Search infrastructure, users, or brands..."
      />

      {/* Stats Section */}
      <div className="p-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          label="Total Brands"
          value={brandCount.toString()}
          icon="apartment"
          iconColor="text-blue-400"
          iconBg="bg-blue-400/10"
          loading={loading}
          footer={`${activeBrandCount} active brands`}
        />
        <StatCard
          label="System Users"
          value={userCount.toString()}
          icon="person_search"
          iconColor="text-purple-400"
          iconBg="bg-purple-400/10"
          loading={loading}
        />
        <StatCard
          label="Open Tickets"
          value={openTickets.toString()}
          icon="confirmation_number"
          iconColor="text-orange-400"
          iconBg="bg-orange-400/10"
          loading={loading}
          footer={
            urgentTickets > 0 ? (
              <span className="text-primary font-bold animate-pulse">
                {urgentTickets} urgent ticket{urgentTickets !== 1 ? 's' : ''} require attention
              </span>
            ) : undefined
          }
        />
        <StatCard
          label="Total Tickets"
          value={ticketStats?.totalTickets.toString() || '0'}
          icon="analytics"
          iconColor="text-green-400"
          iconBg="bg-green-400/10"
          loading={loading}
          footer={
            ticketStats ? (
              <span>
                {ticketStats.resolvedTickets} resolved • {ticketStats.closedTickets} closed
              </span>
            ) : undefined
          }
        />
      </div>

      {/* Chart Section */}
      <div className="px-8 pb-8 flex gap-6">
        {/* Ticket Breakdown */}
        <div className="flex-grow bg-white border border-slate-200 rounded-xl p-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h4 className="text-xl font-bold text-slate-900">Ticket Overview</h4>
              <p className="text-slate-500 text-sm">
                Current ticket status breakdown
              </p>
            </div>
          </div>

          {loading ? (
            <div className="h-48 flex items-center justify-center">
              <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : ticketStats ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {[
                { label: 'Open', value: ticketStats.openTickets, color: 'text-blue-500', bg: 'bg-blue-50' },
                { label: 'In Progress', value: ticketStats.inProgressTickets, color: 'text-amber-500', bg: 'bg-amber-50' },
                { label: 'Waiting', value: ticketStats.waitingCustomerTickets, color: 'text-slate-500', bg: 'bg-slate-50' },
                { label: 'Resolved', value: ticketStats.resolvedTickets, color: 'text-green-500', bg: 'bg-green-50' },
                { label: 'Closed', value: ticketStats.closedTickets, color: 'text-slate-400', bg: 'bg-slate-50' },
              ].map((item) => (
                <div key={item.label} className={`${item.bg} rounded-xl p-4 text-center`}>
                  <p className={`text-3xl font-extrabold ${item.color}`}>{item.value}</p>
                  <p className="text-xs text-slate-500 mt-1 font-medium">{item.label}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="h-48 flex items-center justify-center text-slate-400 text-sm">
              No ticket data available
            </div>
          )}

          {/* Priority breakdown */}
          {ticketStats && (ticketStats.urgentTickets > 0 || ticketStats.highPriorityTickets > 0) && (
            <div className="mt-6 pt-6 border-t border-slate-200 flex items-center gap-6">
              <p className="text-xs text-slate-500 font-bold uppercase tracking-widest">Priority</p>
              {ticketStats.urgentTickets > 0 && (
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                  <span className="text-sm font-bold text-red-500">{ticketStats.urgentTickets} Urgent</span>
                </div>
              )}
              {ticketStats.highPriorityTickets > 0 && (
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-primary" />
                  <span className="text-sm font-bold text-primary">{ticketStats.highPriorityTickets} High</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Side Activity */}
        <aside className="w-80 bg-white border border-slate-200 rounded-xl flex flex-col p-6">
          <div className="flex items-center justify-between mb-6">
            <h4 className="font-bold text-slate-900">Quick Stats</h4>
            <span className="material-symbols-outlined text-slate-400">
              dashboard
            </span>
          </div>
          <div className="space-y-6 overflow-y-auto pr-1 flex-1">
            <ActivityItem
              icon="storefront"
              iconColor="text-blue-500"
              iconBg="bg-blue-500/10"
              title={`${brandCount} Brands`}
              description={`${activeBrandCount} active, ${brandCount - activeBrandCount} inactive`}
              time="Platform total"
            />
            <ActivityItem
              icon="group"
              iconColor="text-purple-500"
              iconBg="bg-purple-500/10"
              title={`${userCount} System Users`}
              description="Admin panel users"
              time="Platform total"
            />
            <ActivityItem
              icon="confirmation_number"
              iconColor="text-orange-500"
              iconBg="bg-orange-500/10"
              title={`${ticketStats?.totalTickets || 0} Total Tickets`}
              description={`${openTickets} open, ${ticketStats?.resolvedTickets || 0} resolved`}
              time="All time"
            />
            <ActivityItem
              icon="priority_high"
              iconColor="text-red-500"
              iconBg="bg-red-500/10"
              title={`${urgentTickets} Urgent`}
              description={`${ticketStats?.highPriorityTickets || 0} high priority`}
              time="Needs attention"
              showLine={false}
            />
          </div>
        </aside>
      </div>

      {/* Footer */}
      <footer className="mt-auto px-8 py-6 border-t border-slate-200 flex items-center justify-between">
        <p className="text-xs text-slate-400">
          © 2024 FoxygenSeo Platform. All rights reserved.
        </p>
        <div className="flex gap-6">
          <button className="text-xs text-slate-400 hover:text-primary transition-colors">
            Documentation
          </button>
          <button className="text-xs text-slate-400 hover:text-primary transition-colors">
            API Status
          </button>
          <button className="text-xs text-slate-400 hover:text-primary transition-colors">
            Privacy
          </button>
        </div>
      </footer>
    </main>
  );
};

export default DashboardPage;
